import { lazy, Suspense, useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Mic, ShieldCheck, Activity, Server, FileText, ArrowRight, Radio, Lock, Play, CalendarClock, Menu, X, Target, TrendingUp, GraduationCap, Smile, ClipboardCheck, Heart, CheckCircle2, Settings2, Tag, CloudOff, Building2, Layers, Zap, Quote, Bluetooth, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Logo } from "../components/Logo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { LanguageSelector } from "../components/LanguageSelector";
import { ThemeToggle } from "../components/ThemeToggle";
import { StickyBookDemo } from "../components/StickyBookDemo";
import { BookDemoProvider, useBookDemo } from "../context/BookDemoContext";
import { useLanguage } from "../context/LanguageContext";
import { useApplyTheme } from "../context/ThemeContext";
import pendantLedOn from "../assets/pendant-led-on.webp";
import pendantLedOff from "../assets/pendant-led-off.webp";
import dashboardOverview from "../assets/dashboard-overview.webp";
import dashboardEmployeeProgress from "../assets/dashboard-employee-progress.webp";
// product-01 (Marriott branding) and product-02 (real-airline-styled uniform) are
// deliberately not imported here -- publishing either would imply that brand
// endorses/uses EXOPILOT, which needs their permission first. product-03/04/05 are
// generic/EXOPILOT-branded, safe to reuse across multiple spots -- each spot below
// gets its own import name (per the request that duplicated files are fine as long
// as the name isn't shared) even where the underlying file repeats.
import productHeroBanner from "../assets/product-01.webp";
import productVideoBackground from "../assets/product-03.webp";
import productWearableDevice from "../assets/product-05.webp";
import productHotelsBanner from "../assets/product-05.webp";
import productFrontDesk from "../assets/product-04.webp";
import productConcierge from "../assets/product-02.webp";
import productFnbDining from "../assets/product-01.webp";

// Below-the-fold / on-demand pieces are split out of the main bundle. The India map
// alone ships ~200 KB of state outlines, and the chat widget + demo modal are only
// needed once the visitor interacts, so none of them should delay first render.
const IndiaBranchMap = lazy(() => import("../components/IndiaBranchMap").then((m) => ({ default: m.IndiaBranchMap })));
const ChatWidget = lazy(() => import("../components/ChatWidget").then((m) => ({ default: m.ChatWidget })));
const BookDemoModal = lazy(() => import("../components/BookDemoModal").then((m) => ({ default: m.BookDemoModal })));

// Renders children only once the placeholder is near the viewport.
function RenderWhenNear({ children, minHeight = 300 }) {
  const ref = useRef(null);
  const [near, setNear] = useState(false);
  useEffect(() => {
    if (!("IntersectionObserver" in window)) { setNear(true); return; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setNear(true); io.disconnect(); } }, { rootMargin: "600px" });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  return <div ref={ref} style={near ? undefined : { minHeight }}>{near && <Suspense fallback={null}>{children}</Suspense>}</div>;
}

// True once the page has loaded and the browser is idle (or the visitor interacts).
function useIdleReady() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const go = () => setReady(true);
    const events = ["pointerdown", "scroll", "keydown", "touchstart"];
    events.forEach((e) => window.addEventListener(e, go, { once: true, passive: true }));
    const t = setTimeout(go, 6000);
    return () => { clearTimeout(t); events.forEach((e) => window.removeEventListener(e, go)); };
  }, []);
  return ready;
}

// True once the element has scrolled into view; stays true afterwards (a
// one-shot reveal, not a repeating one). Falls back to already-visible when
// IntersectionObserver isn't available.
function useInView(options) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!("IntersectionObserver" in window)) { setInView(true); return; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); io.disconnect(); } }, options);
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return [ref, inView];
}

// Fades + rises children into place once they scroll into view, instead of
// everything on the page being visible (and animated, if at all) on load --
// used throughout the landing page so scrolling down reads as the product
// story unfolding rather than a static wall of sections. `delay` (ms) staggers
// siblings, e.g. cards in the same grid revealing one after another.
function Reveal({ children, className = "", delay = 0, as: Tag = "div", ...rest }) {
  const [ref, inView] = useInView({ threshold: 0.15, rootMargin: "0px 0px -60px 0px" });
  return (
    <Tag ref={ref} className={`reveal ${inView ? "in-view" : ""} ${className}`} style={{ transitionDelay: inView ? `${delay}ms` : undefined }} {...rest}>
      {children}
    </Tag>
  );
}

// Renders a stat string (e.g. "94.2", "100%", "<1 min", "+9 pts") and, once it
// scrolls into view, counts the leading number up from 0 to its real value
// (keeping any prefix/suffix text static) instead of the number just being
// there. Values of 1 or less skip the count -- animating "0 -> 1" reads as a
// glitch, not real motion.
function StatValue({ value }) {
  const [ref, inView] = useInView({ threshold: 0.4 });
  const [display, setDisplay] = useState(0);
  const match = String(value).match(/^(\D*)(\d[\d.]*)(.*)$/);
  const target = match ? parseFloat(match[2]) : null;
  const decimals = match && match[2].includes(".") ? match[2].split(".")[1].length : 0;

  useEffect(() => {
    if (!inView || target === null || target <= 1) return;
    let raf;
    const duration = 1200;
    const start = performance.now();
    const step = (now) => {
      const progress = Math.min(1, (now - start) / duration);
      setDisplay(target * (1 - Math.pow(1 - progress, 3)));
      if (progress < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, target]);

  if (!match || target === null || target <= 1) return <span ref={ref}>{value}</span>;
  return <span ref={ref}>{match[1]}{display.toFixed(decimals)}{match[3]}</span>;
}

const STEP_ICONS = [Mic, Server, Activity];
const FEATURE_ICONS = [FileText, Radio, Lock, ShieldCheck];
const COACHING_ICONS = [Target, TrendingUp, GraduationCap];
const DIMENSION_ICONS = [Smile, ClipboardCheck, Heart, CheckCircle2];
const ROLLOUT_ICONS = [Settings2, Server, Tag, TrendingUp];
const TRUST_STAT_ICONS = [CloudOff, Building2, Layers, Zap];

// Hero's "read nothing, just watch" demo: a looping (CSS-only, ~6.4s cycle)
// sequence showing the actual product flow -- pendant captures audio, audio
// travels only as far as the local server and visibly stops there, and only a
// small score badge continues on to the console. Meant to make the privacy
// story ("only scores leave your premises") something you see happen, not a
// sentence you have to read. Runs on every breakpoint, including mobile, where
// the static dashboard screenshots to its left are hidden for space.
// A small SVG waveform, continuously scrolling -- two copies of the same
// A small circular icon node in the flowchart (bluetooth / server). Everything
// here is permanently visible -- no fade-in, nothing hidden -- the only motion
// in the whole diagram is the .flow-pulse travelling along the connectors
// between nodes (see FlowConnector), same technique as the "How it works"
// section's flow diagram further down the page.
function FlowIconNode({ icon: Icon, label }) {
  return (
    <div className="shrink-0 flex flex-col items-center gap-2 w-16">
      <span className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white dark:bg-neutral-900 border-2 border-neutral-200 dark:border-neutral-800 flex items-center justify-center">
        <Icon className="w-4 h-4 sm:w-[18px] sm:h-[18px] text-neutral-500 dark:text-neutral-400" />
      </span>
      <span className="font-mono-data text-[10px] uppercase tracking-[0.12em] text-neutral-400 dark:text-neutral-500 text-center whitespace-nowrap">{label}</span>
    </div>
  );
}

// A short connecting line with a bright segment continuously travelling along
// it (reuses .flow-pulse) -- signal motion without ever hiding the nodes on
// either end.
function FlowConnector({ className = "flex-1 min-w-[28px] sm:min-w-[40px]", delay = "0s" }) {
  return (
    <div className={`relative ${className} h-px shrink-0 self-center bg-neutral-200 dark:bg-neutral-800 overflow-hidden`} aria-hidden="true">
      <div className="flow-pulse absolute inset-y-0 w-1/2 bg-gradient-to-r from-transparent via-neutral-500 dark:via-neutral-300 to-transparent" style={{ animationDelay: delay }} />
    </div>
  );
}

// Fork: the server's single output visibly splits into two, drawn as a small
// circular hub with rounded-elbow lines branching out to each target. Built
// from plain CSS borders + border-radius (the standard org-chart-connector
// technique) instead of an SVG path -- a square SVG viewBox stretched
// non-uniformly to fit this short, tall container was distorting the curves
// and the moving dot into streaks. Borders don't have that problem: they
// follow the box's actual rendered size exactly. Two stacked halves
// (flex-1 each) share one continuous left border down the full height (the
// vertical stem from the hub) and each adds its own top or bottom border,
// rounded at the shared corner, to peel off toward its row. rtl: variants
// mirror the corner to the opposite side rather than transforming the whole
// block, since there's no SVG geometry to flip here.
function ForkConnector() {
  return (
    <div className="shrink-0 flex items-stretch" aria-hidden="true">
      <div className="flex flex-col items-center justify-center pr-1 rtl:pr-0 rtl:pl-1">
        <span className="w-3.5 h-3.5 rounded-full bg-white dark:bg-neutral-900 border-2 border-neutral-300 dark:border-neutral-700" />
      </div>
      <div className="w-6 sm:w-8 flex flex-col">
        <div className="flex-1 border-l-2 rtl:border-l-0 rtl:border-r-2 border-t-2 border-neutral-200 dark:border-neutral-800 rounded-tl-2xl rtl:rounded-tl-none rtl:rounded-tr-2xl" />
        <div className="flex-1 border-l-2 rtl:border-l-0 rtl:border-r-2 border-b-2 border-neutral-200 dark:border-neutral-800 rounded-bl-2xl rtl:rounded-bl-none rtl:rounded-br-2xl" />
      </div>
    </div>
  );
}

function HeroLoop() {
  const { t } = useLanguage();
  return (
    <div className="mt-14 w-full" data-testid="hero-loop">
      <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white/70 dark:bg-neutral-900/50 backdrop-blur-xl p-5 sm:p-7">
        {/* Left to right: pendant -> BLE -> local server, which then feeds both the
            console score and an AI-written suggestion together (the local server
            produces both from the same pass, not one after the other -- shown as a
            fork, not a fourth step in line). Full width of the card, connectors
            flex to fill it; scrolls horizontally on narrow phones rather than being
            squeezed or restacked -- a flowchart reads left to right or not at all. */}
        <div className="flex items-center gap-2 sm:gap-3 overflow-x-auto pb-1 -mx-1 px-1">
          {/* The actual pendant, not an icon standing in for it -- just the photo,
              no background tile behind it. */}
          <div className="shrink-0 flex flex-col items-center gap-2">
            <span className="hero-record-pulse relative w-16 h-40 sm:w-20 sm:h-52 rounded-xl overflow-hidden">
              <img src={pendantLedOff} alt="EXOPILOT pendant" className="absolute inset-0 w-full h-full object-cover" />
              <img src={pendantLedOn} alt="" aria-hidden="true" className="hero-led-blink absolute inset-0 w-full h-full object-cover" />
            </span>
            <span className="font-mono-data text-[10px] uppercase tracking-[0.12em] text-neutral-400 dark:text-neutral-500">{t.hero.loopPendantLabel}</span>
          </div>

          <FlowConnector />
          <FlowIconNode icon={Bluetooth} label={t.hero.loopBleLabel} />
          <FlowConnector />
          <FlowIconNode icon={Server} label={t.hero.loopServerLabel} />

          {/* Fork: the server's single output splits into two branches at a small
              hub, leading to the score and the AI suggestion side by side --
              visually "the same part", not a chained next step. */}
          <div className="shrink-0 flex items-stretch">
            <ForkConnector />
            <div className="flex flex-col gap-3 sm:gap-4 justify-center">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="shrink-0 flex flex-col items-center gap-1.5">
                  <span className="w-16 h-11 sm:w-[4.5rem] sm:h-12 rounded-xl bg-white dark:bg-neutral-900 border-2 border-neutral-200 dark:border-neutral-800 flex items-center justify-center">
                    <span className="font-heading text-lg font-medium">94.2</span>
                  </span>
                  <span className="font-mono-data text-[10px] uppercase tracking-[0.12em] text-neutral-400 dark:text-neutral-500 whitespace-nowrap">{t.hero.loopConsole}</span>
                </div>
              </div>

              {/* Extra feature, shown rather than described: the same AI-written
                  coaching suggestion the AI Coaching section further down explains
                  in words, rendered here as an actual chat bubble. */}
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="shrink-0 w-48 sm:w-56">
                  <div className="relative rounded-2xl rounded-bl-sm border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-4 py-3 shadow-sm">
                    <p className="flex items-center gap-1.5 font-mono-data text-[10px] uppercase tracking-[0.12em] text-neutral-400 dark:text-neutral-500 mb-1.5">
                      <Sparkles className="w-3 h-3 shrink-0" /> {t.hero.loopSuggestionLabel}
                    </p>
                    <p className="text-sm text-neutral-700 dark:text-neutral-200 leading-snug">{t.hero.loopSuggestionText}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <p className="mt-4 text-xs text-neutral-500 dark:text-neutral-400 max-w-sm">{t.hero.loopCaption}</p>
    </div>
  );
}

// The two stacked "browser window" screenshots with their rise-in entrance.
// Rendered twice in the hero (see LandingInner): once right under the headline
// for mobile/tablet, once in the side column for desktop -- CSS alone can't
// move it from "after the headline" to "beside the headline" at a breakpoint,
// so each copy is just shown/hidden with lg:hidden / hidden lg:block instead
// of trying to reorder one shared instance.
function HeroPreviewCard({ className = "" }) {
  return (
    <div className={`relative ${className}`} data-testid="hero-preview-card">
      <div className="relative pt-4 pr-4 sm:pt-6 sm:pr-6 rtl:pr-0 rtl:pl-4 sm:rtl:pl-6">
        <div className="absolute top-0 right-0 rtl:right-auto rtl:left-0 w-[88%] rise-in rise-in-back">
        <div className="rounded-lg overflow-hidden border border-neutral-200 dark:border-neutral-800 shadow-lg rotate-2 bg-white dark:bg-neutral-900">
          <div className="flex items-center gap-1.5 px-3.5 py-2.5 bg-neutral-100 dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700">
            <span className="w-2 h-2 rounded-full bg-red-300" />
            <span className="w-2 h-2 rounded-full bg-amber-300" />
            <span className="w-2 h-2 rounded-full bg-green-300" />
          </div>
          <img decoding="async" width="1200" height="750" src={dashboardOverview} alt="EXOPILOT console overview dashboard" className="w-full h-auto block aspect-[16/10] object-cover object-top" />
        </div>
        </div>
        <div className="relative rise-in rise-in-front">
        <div className="float-slow relative rounded-lg overflow-hidden border border-neutral-200 dark:border-neutral-800 shadow-2xl bg-white dark:bg-neutral-900 -rotate-1">
          <div className="flex items-center gap-1.5 px-3.5 py-2.5 bg-neutral-100 dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700">
            <span className="w-2 h-2 rounded-full bg-red-400" />
            <span className="w-2 h-2 rounded-full bg-amber-400" />
            <span className="w-2 h-2 rounded-full bg-green-400" />
            <span className="ml-2 text-[10px] text-neutral-400 font-mono-data">console.exopilot.io</span>
          </div>
          <img decoding="async" width="1200" height="750" src={dashboardEmployeeProgress} alt="EXOPILOT employee score trend dashboard" className="w-full h-auto block aspect-[16/10] object-cover object-top" />
        </div>
        </div>
      </div>
    </div>
  );
}

function ConversationReplay() {
  const { t } = useLanguage();
  const [activeStep, setActiveStep] = useState(0);
  const steps = t.replay.steps;
  const stepRefs = useRef([]);
  const scoreTone = (s) => (s >= 85 ? "text-green-700 dark:text-green-500" : s >= 70 ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400");

  useEffect(() => {
    // Auto-advances the active step as the page scrolls -- whichever step row is
    // nearest the vertical center of the viewport becomes active, without needing
    // a click. rootMargin shrinks the observed area to a thin band around center
    // (-40% top/bottom), so a step activates right as it crosses the middle.
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        const nearest = visible.reduce((a, b) => (Math.abs(a.boundingClientRect.top) < Math.abs(b.boundingClientRect.top) ? a : b));
        const idx = stepRefs.current.indexOf(nearest.target);
        if (idx !== -1) setActiveStep(idx);
      },
      { rootMargin: "-40% 0px -40% 0px", threshold: 0 }
    );
    stepRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [steps.length]);

  return (
    <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden" data-testid="conversation-replay">
      <div className="px-6 py-4 border-b border-neutral-100 dark:border-neutral-800 flex items-center justify-between">
        <span className="inline-flex items-center gap-2 text-sm font-medium">
          <Play className="w-4 h-4" fill="currentColor" /> {t.replay.playerLabel}
        </span>
        <span className="text-[11px] text-neutral-500 dark:text-neutral-400 font-mono-data">03:41</span>
      </div>
      <div className="h-1 bg-neutral-100 dark:bg-neutral-800 relative">
        <div className="h-full bg-[#111] dark:bg-white" style={{ width: `${((activeStep + 1) / steps.length) * 100}%` }} />
      </div>
      <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
        {steps.map((s, i) => (
          <button key={i} ref={(el) => { stepRefs.current[i] = el; }} onClick={() => setActiveStep(i)} data-testid={`replay-step-${i}`}
            className={`w-full text-left rtl:text-right px-6 py-4 flex items-start gap-4 transition-colors ${activeStep === i ? "bg-neutral-50 dark:bg-neutral-800" : "hover:bg-neutral-50/60 dark:hover:bg-neutral-800/60"}`}>
            <span className="text-[11px] font-mono-data text-neutral-500 dark:text-neutral-400 mt-0.5 shrink-0 w-10">{s.time}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-medium">{s.label}</p>
                <span className={`font-heading text-lg font-light shrink-0 ${scoreTone(s.score)}`}>{s.score}</span>
              </div>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed mt-1">{s.note}</p>
            </div>
          </button>
        ))}
      </div>
      <p className="px-6 py-3 text-[11px] text-neutral-500 dark:text-neutral-400 border-t border-neutral-100 dark:border-neutral-800">{t.replay.disclaimer}</p>
    </div>
  );
}

// One "what gets measured" card: reveals in, and only grows its score bar (and
// counts up its number) once it's actually visible -- previously the bar
// animated on mount regardless of scroll position, so by the time a visitor
// reached this section (well below the fold) the animation had already run to
// completion and there was nothing left to see.
function DimensionCard({ d, i }) {
  const [ref, inView] = useInView({ threshold: 0.3, rootMargin: "0px 0px -40px 0px" });
  const Icon = DIMENSION_ICONS[i];
  return (
    <div ref={ref} className={`reveal ${inView ? "in-view" : ""} group bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg p-6 hover:border-[#111] dark:hover:border-white transition-colors`} style={{ transitionDelay: inView ? `${i * 90}ms` : undefined }}>
      <div className="flex items-start justify-between">
        <span className="w-9 h-9 rounded-md bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4 text-[#111] dark:text-white" />
        </span>
        <span className="font-heading text-2xl font-light"><StatValue value={d.score} /></span>
      </div>
      <h3 className="text-sm font-medium mt-4">{d.name}</h3>
      <div className="h-1.5 bg-neutral-100 dark:bg-neutral-800 rounded-full overflow-hidden mt-3">
        <div
          className={`h-full bg-[#111] dark:bg-white rounded-full group-hover:bg-neutral-600 dark:group-hover:bg-neutral-300 transition-colors ${inView ? "score-bar" : ""}`}
          style={{ width: inView ? `${d.score}%` : "0%", animationDelay: `${i * 0.12}s` }}
        />
      </div>
      <p className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed mt-4">{d.desc}</p>
    </div>
  );
}

function LandingInner() {
  const { t, dir } = useLanguage();
  const { openDemo } = useBookDemo();
  useApplyTheme();
  const idleReady = useIdleReady();
  const [contact, setContact] = useState({ name: "", email: "", message: "" });
  const [sendingContact, setSendingContact] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const setContactField = (k) => (e) => setContact((c) => ({ ...c, [k]: e.target.value }));

  const navLinks = [
    { href: "#how", label: t.nav.how },
    { href: "#platform", label: t.nav.platform },
    { href: "#privacy", label: t.nav.privacy },
    { to: "/trust", label: t.nav.trust },
    { href: "#contact", label: t.nav.contact },
  ];

  const submitContact = async (e) => {
    e.preventDefault();
    setSendingContact(true);
    try {
      // POST /api/contact (cloud-backend/server.py): emails the submission to the
      // team and triggers a visitor-facing auto-reply via Resend. Returns a normal
      // 503 rather than crashing when RESEND_API_KEY isn't set on this deployment.
      await api.post("/contact", contact);
      toast.success(t.contact.toastTitle, { description: t.contact.toastDesc });
      setContact({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSendingContact(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f9f9f8] dark:bg-neutral-950 text-[#111] dark:text-neutral-100" dir={dir}>
      <header className="fixed top-0 inset-x-0 z-50 bg-[#f9f9f8]/85 dark:bg-neutral-950/85 backdrop-blur-xl border-b border-neutral-200 dark:border-neutral-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
          <Link to="/" data-testid="nav-logo" className="shrink-0"><Logo /></Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-neutral-600 dark:text-neutral-300">
            {navLinks.map((l) => l.to ? (
              <Link key={l.label} to={l.to} data-testid="nav-trust-link" className="hover:text-[#111] dark:hover:text-white transition-colors">{l.label}</Link>
            ) : (
              <a key={l.label} href={l.href} className="hover:text-[#111] dark:hover:text-white transition-colors">{l.label}</a>
            ))}
          </nav>
          <div className="flex items-center gap-1 sm:gap-3">
            {/* Language/theme controls move into the mobile menu below ~640px --
                Logo + these + the Get Started pill + hamburger don't all fit in one
                row on a 320-360px phone otherwise. */}
            <div className="hidden sm:flex items-center gap-1.5 sm:gap-3">
              <LanguageSelector compact />
              <ThemeToggle />
            </div>
            <Link to="/login" data-testid="nav-login-link" className="hidden sm:inline text-sm text-neutral-600 dark:text-neutral-300 hover:text-[#111] dark:hover:text-white transition-colors px-3 py-2">{t.nav.signIn}</Link>
            <Link to="/register" data-testid="nav-register-link" className="text-sm bg-[#111] dark:bg-white text-white dark:text-[#111] rounded-full px-3.5 sm:px-5 py-2 hover:bg-neutral-800 dark:hover:bg-neutral-200 transition-colors whitespace-nowrap">{t.nav.getStarted}</Link>
            <button
              data-testid="mobile-nav-toggle"
              onClick={() => setMobileNavOpen((o) => !o)}
              aria-label={mobileNavOpen ? "Close menu" : "Open menu"}
              className="md:hidden p-2 -mr-2 rtl:mr-0 rtl:-ml-2 rounded-md text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors shrink-0"
            >
              {mobileNavOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {mobileNavOpen && (
          <nav data-testid="mobile-nav-menu" className="md:hidden border-t border-neutral-200 dark:border-neutral-800 bg-[#f9f9f8] dark:bg-neutral-950 px-4 py-3 flex flex-col">
            {navLinks.map((l) => l.to ? (
              <Link key={l.label} to={l.to} onClick={() => setMobileNavOpen(false)} className="py-2.5 text-sm text-neutral-700 dark:text-neutral-200">{l.label}</Link>
            ) : (
              <a key={l.label} href={l.href} onClick={() => setMobileNavOpen(false)} className="py-2.5 text-sm text-neutral-700 dark:text-neutral-200">{l.label}</a>
            ))}
            <Link to="/login" onClick={() => setMobileNavOpen(false)} className="py-2.5 text-sm text-neutral-700 dark:text-neutral-200 border-t border-neutral-100 dark:border-neutral-800 mt-1 pt-3.5">{t.nav.signIn}</Link>
            <div className="sm:hidden flex items-center gap-3 border-t border-neutral-100 dark:border-neutral-800 mt-1 pt-3.5">
              <LanguageSelector />
              <ThemeToggle />
            </div>
          </nav>
        )}
      </header>
      <main>

      <section className="relative pt-32 sm:pt-40 pb-24 px-6 overflow-hidden">
        <div className="pointer-events-none absolute -top-40 right-[-160px] w-[620px] h-[620px] rounded-full aurora-a" aria-hidden="true" />
        <div className="pointer-events-none absolute top-72 left-[-220px] w-[540px] h-[540px] rounded-full aurora-b" aria-hidden="true" />
        <div className="relative max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <p className="font-mono-data text-[11px] sm:text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400 mb-6">{t.hero.eyebrow}</p>
            <h1 className="font-heading font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05]">
              {t.hero.titleLine}
              <span className="font-serif-accent italic text-neutral-500 dark:text-neutral-400"> {t.hero.titleAccent}</span>
            </h1>

            {/* Screenshot sits right under the headline on mobile/tablet; the desktop
                copy further down (hidden here via lg:hidden) takes over at lg, sitting
                beside the text instead. */}
            <HeroPreviewCard className="mt-8 lg:hidden max-w-sm mx-auto sm:max-w-none sm:mx-0" />

            <p className="mt-8 text-base md:text-lg text-neutral-600 dark:text-neutral-400 max-w-xl leading-relaxed">{t.hero.subtitle}</p>
            <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4 flex-wrap">
              <Link to="/register" data-testid="hero-cta" className="inline-flex items-center justify-center gap-2 bg-[#111] dark:bg-white text-white dark:text-[#111] rounded-full px-7 py-3.5 text-sm font-medium hover:bg-neutral-800 dark:hover:bg-neutral-200 transition-colors whitespace-nowrap w-full sm:w-auto">
                {t.hero.ctaConsole} <ArrowRight className="w-4 h-4 shrink-0 rtl:rotate-180" />
              </Link>
              <button onClick={openDemo} data-testid="hero-book-demo-cta"
                className="inline-flex items-center justify-center gap-2 border border-neutral-300 dark:border-neutral-700 rounded-full px-7 py-3.5 text-sm font-medium bg-white sm:bg-transparent dark:bg-neutral-900 dark:sm:bg-transparent hover:bg-white dark:hover:bg-neutral-900 transition-colors whitespace-nowrap w-full sm:w-auto">
                <CalendarClock className="w-4 h-4 shrink-0" /> {t.hero.ctaBookDemo}
              </button>
              <a href="#how" className="text-sm text-neutral-600 dark:text-neutral-400 hover:text-[#111] dark:hover:text-white transition-colors border-b border-neutral-300 dark:border-neutral-700 pb-0.5 self-center">{t.hero.ctaArchitecture}</a>
            </div>
            <div className="mt-12 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap" data-testid="trust-chips">
              {t.trustChips.map((c) => (
                <span key={c} className="inline-flex items-center justify-center gap-1.5 text-center text-xs text-neutral-600 dark:text-neutral-300 bg-white/70 dark:bg-neutral-900/70 backdrop-blur-xl border border-neutral-200 dark:border-neutral-800 rounded-full px-3.5 py-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-neutral-500 dark:text-neutral-400 shrink-0" /> {c}
                </span>
              ))}
            </div>

          </div>

          {/* Desktop only -- the mobile/tablet copy sits right under the headline
              above instead (same HeroPreviewCard, different spot in the markup). */}
          <HeroPreviewCard className="hidden lg:block lg:col-span-5" />
        </div>

        {/* Flowchart card hidden  -- HeroLoop component and its
            translations/CSS left in place, just not rendered.
        <div className="relative max-w-6xl mx-auto">
          <HeroLoop />
        </div>
        */}
      </section>

      <section className="px-6 pb-16">
        <span className="sm:hidden block text-center text-xs text-neutral-500 dark:text-neutral-400 whitespace-nowrap mb-3">{t.industriesLabel}</span>
        <div className="max-w-6xl mx-auto border-y border-neutral-200 dark:border-neutral-800 py-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-8" data-testid="industries-marquee">
          <span className="hidden sm:inline text-xs text-neutral-500 dark:text-neutral-400 shrink-0 whitespace-nowrap">{t.industriesLabel}</span>
          <div className="relative flex-1 overflow-hidden marquee-mask w-full sm:w-auto">
            <div className="flex w-max marquee-track">
              {[...t.industries, ...t.industries].map((it, i) => (
                <span key={`${it}-${i}`} className="text-xs uppercase tracking-[0.18em] text-neutral-400 whitespace-nowrap pr-16">{it}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 pb-16 sm:pb-20 lg:pb-28">
        <Reveal as="div" className="max-w-6xl mx-auto">
          <div className="relative rounded-lg overflow-hidden border border-neutral-200 dark:border-neutral-800 aspect-[4/5] sm:aspect-[16/9] lg:aspect-[21/9]">
            <img loading="lazy" decoding="async" width="1200" height="800" src={productHeroBanner} alt="EXOPILOT pendant in use on the service floor" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/40" />
            <div className="absolute bottom-4 left-4 right-4 sm:bottom-8 sm:left-8 sm:right-8 grid grid-cols-3 gap-2 sm:flex sm:flex-wrap sm:gap-6 text-white">
              {t.heroStats.map(([v, l], i) => (
                <Reveal key={l} delay={i * 100} className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-lg px-2.5 py-2.5 sm:px-6 sm:py-4">
                  <div className="font-heading text-lg sm:text-3xl font-light"><StatValue value={v} /></div>
                  <div className="text-[10px] sm:text-xs text-white/80 mt-1 leading-snug">{l}</div>
                </Reveal>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      <section id="how" className="px-6 py-16 sm:py-20 lg:py-28 bg-[#111] dark:bg-black text-[#ededed]">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-400 mb-4">{t.how.eyebrow}</p>
            <h2 className="font-heading font-light text-lg md:text-lg max-w-lg text-neutral-300">{t.how.heading}</h2>
          </Reveal>

          {/* Flow diagram: the same three steps as nodes on a connecting line first,
              with a pulse of light travelling along it -- the pendant -> local
              server -> console signal path made visible -- before the detail cards
              below spell each step out in words. */}
          <Reveal delay={120} className="hidden md:block mt-14" as="div">
            <div className="relative flex items-center justify-between max-w-2xl mx-auto px-4">
              <div className="absolute top-7 left-14 right-14 h-px bg-neutral-800 overflow-hidden" aria-hidden="true">
                <div className="h-full w-1/4 bg-gradient-to-r from-transparent via-white/70 to-transparent flow-pulse" />
              </div>
              {t.how.steps.map((s, i) => {
                const Icon = STEP_ICONS[i];
                return (
                  <div key={s.n} className="relative z-10 flex flex-col items-center gap-2.5 bg-[#111] px-3">
                    <span className="w-14 h-14 rounded-full bg-[#161616] border border-neutral-700 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-white" />
                    </span>
                    <span className="font-mono-data text-[11px] text-neutral-500">{s.n}</span>
                  </div>
                );
              })}
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-neutral-800 border border-neutral-800 rounded-lg overflow-hidden mt-8 md:mt-8">
            {t.how.steps.map((s, i) => {
              const Icon = STEP_ICONS[i];
              return (
                <Reveal key={s.n} delay={i * 100} className="bg-[#161616] p-10">
                  <div className="flex items-center justify-between mb-10">
                    <Icon className="w-5 h-5 text-neutral-400 md:hidden" />
                    <span className="font-mono-data text-xs text-neutral-400 md:hidden">{s.n}</span>
                  </div>
                  <h3 className="font-heading text-lg font-medium mb-3">{s.title}</h3>
                  <p className="text-sm text-neutral-400 leading-relaxed">{s.text}</p>
                </Reveal>
              );
            })}
          </div>

          <Reveal className="relative rounded-lg overflow-hidden border border-neutral-800 mt-14 aspect-[4/5] sm:aspect-[16/9] lg:aspect-[21/9]" data-testid="how-it-works-video">
            <img loading="lazy" decoding="async" width="1200" height="800" src={productVideoBackground} alt="EXOPILOT pendant in use" className="absolute inset-0 w-full h-full object-cover opacity-60" />
            <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center gap-4 sm:gap-5 px-6">
              <button
                data-testid="play-video-button"
                onClick={() => toast(t.how.videoToast, { description: t.how.videoToastDesc })}
                aria-label={t.how.videoTitle}
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white text-[#111] flex items-center justify-center hover:scale-105 transition-transform shadow-lg">
                <Play className="w-6 h-6 sm:w-7 sm:h-7 ml-1 rtl:ml-0 rtl:mr-1" fill="currentColor" />
              </button>
              <div className="text-center">
                <p className="text-sm font-medium text-white">{t.how.videoTitle}</p>
                <p className="text-xs text-neutral-400 mt-1 font-mono-data">{t.how.videoCaption}</p>
              </div>
            </div>
            <div className="absolute top-5 left-5 rtl:left-auto rtl:right-5 inline-flex items-center gap-1.5 text-[11px] text-white/90 bg-white/10 backdrop-blur-xl border border-white/20 rounded-full px-3 py-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500" /> {t.how.videoBadge}
            </div>
          </Reveal>
          <div className="flex justify-center mt-8">
            <button onClick={openDemo} data-testid="video-book-demo-cta"
              className="inline-flex items-center gap-2 bg-white text-[#111] rounded-full px-7 py-3 text-sm font-medium hover:bg-neutral-200 transition-colors">
              <CalendarClock className="w-4 h-4" /> {t.hero.ctaBookDemo}
            </button>
          </div>
        </div>
      </section>

      <section id="platform" className="px-6 py-16 sm:py-20 lg:py-28">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400 mb-4">{t.platform.eyebrow}</p>
            <h2 className="font-heading font-light text-lg max-w-lg text-neutral-600 dark:text-neutral-400">{t.platform.heading}</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-14">
            {t.platform.features.map((f, i) => {
              const Icon = FEATURE_ICONS[i];
              return (
                <Reveal key={f.title} delay={i * 80} className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg p-8 hover:-translate-y-0.5 hover:shadow-lg hover:border-neutral-300 dark:hover:border-neutral-700 transition-[transform,box-shadow,border-color] duration-200">
                  <span className="w-10 h-10 rounded-md bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 flex items-center justify-center mb-6">
                    <Icon className="w-5 h-5 text-[#111] dark:text-white" />
                  </span>
                  <h3 className="font-heading text-lg font-medium mb-2">{f.title}</h3>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed">{f.text}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="px-6 pb-16 sm:pb-20 lg:pb-28">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <Reveal className="md:col-span-5">
            <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400 mb-4">{t.aiCoaching.eyebrow}</p>
            <h2 className="font-heading font-light text-3xl tracking-tight leading-snug">{t.aiCoaching.heading}</h2>
            <p className="mt-5 text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed">{t.aiCoaching.intro}</p>
            <ul className="mt-7 space-y-5">
              {t.aiCoaching.bullets.map((b, i) => {
                const Icon = COACHING_ICONS[i];
                return (
                  <li key={i} className="flex gap-3.5 text-sm text-neutral-600 dark:text-neutral-300">
                    <span className="w-8 h-8 rounded-md bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4 text-[#111] dark:text-white" />
                    </span>
                    <span className="pt-1">{b}</span>
                  </li>
                );
              })}
            </ul>
          </Reveal>
          <div className="md:col-span-7 space-y-4" data-testid="ai-coaching-card">
            {t.aiCoaching.notes.map((note, i) => (
              <Reveal key={i} delay={i * 100} className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg p-7" data-testid={`ai-coaching-note-${i}`}>
                <div className="flex items-center justify-between pb-4 border-b border-neutral-100 dark:border-neutral-800">
                  <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">{t.aiCoaching.cardLabel}</span>
                  <span className="text-[11px] text-neutral-500 dark:text-neutral-400 font-mono-data">{note.name}</span>
                </div>
                <p className="text-sm text-neutral-700 dark:text-neutral-200 leading-relaxed pt-4">{note.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 pb-16 sm:pb-20 lg:pb-28">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <Reveal className="md:col-span-5">
            <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400 mb-4">{t.replay.eyebrow}</p>
            <h2 className="font-heading font-light text-3xl tracking-tight leading-snug">{t.replay.heading}</h2>
            <p className="mt-5 text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed">{t.replay.intro}</p>
          </Reveal>
          <Reveal delay={120} className="md:col-span-7">
            <ConversationReplay />
          </Reveal>
        </div>
      </section>

      <section id="privacy" className="relative px-6 pb-16 sm:pb-20 lg:pb-28 overflow-hidden">
        <div className="pointer-events-none absolute top-10 right-[-200px] w-[500px] h-[500px] rounded-full aurora-c" aria-hidden="true" />
        <div className="relative max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <Reveal className="md:col-span-6">
            <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400 mb-4">{t.privacySection.eyebrow}</p>
            <h2 className="font-heading font-light text-3xl tracking-tight leading-snug">{t.privacySection.heading}<br />{t.privacySection.headingAccent}</h2>
            <ul className="mt-8 space-y-4 text-sm text-neutral-600 dark:text-neutral-300">
              {t.privacySection.bullets.map((b, i) => (
                <li key={i} className="flex gap-3"><ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-neutral-800 dark:text-neutral-200" /> {b}</li>
              ))}
            </ul>
            <Link to="/privacy" data-testid="privacy-policy-link" className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-[#111] dark:text-white border-b border-neutral-400 dark:border-neutral-600 pb-0.5 hover:border-[#111] dark:hover:border-white transition-colors">
              {t.privacySection.readFull} <ArrowRight className="w-4 h-4 rtl:rotate-180" />
            </Link>
          </Reveal>
          <Reveal delay={120} className="md:col-span-6">
            <img loading="lazy" decoding="async" width="1200" height="800" src={productWearableDevice} alt="EXOPILOT wearable device" className="rounded-lg border border-neutral-200 dark:border-neutral-800 object-cover w-full aspect-[3/2]" />
          </Reveal>
        </div>
      </section>

      <section className="px-6 pb-16 sm:pb-20 lg:pb-28" id="trust">
        <Reveal as="div" className="max-w-6xl mx-auto bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg p-9 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-8" data-testid="trust-center-teaser">
          {/* Mobile: icon + eyebrow share a row (items-center wraps them together),
              then heading/description drop to their own full-width line below (the
              "basis-full" div forces the flex-wrap break) starting flush left, not
              indented under the icon. Desktop (sm:): reverts to icon beside one
              text column, eyebrow included, as before. */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-3 sm:flex-nowrap sm:items-start">
            <span className="w-12 h-12 rounded-lg bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 flex items-center justify-center shrink-0">
              <ShieldCheck className="w-6 h-6 text-[#111] dark:text-white" />
            </span>
            <p className="sm:hidden font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400">{t.trustCenterTeaser.eyebrow}</p>
            <div className="basis-full sm:basis-auto">
              <p className="hidden sm:block font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400 mb-2">{t.trustCenterTeaser.eyebrow}</p>
              <h2 className="font-heading font-light text-2xl tracking-tight">{t.trustCenterTeaser.heading}</h2>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2 max-w-lg leading-relaxed">{t.trustCenterTeaser.text}</p>
            </div>
          </div>
          <Link to="/trust" data-testid="trust-center-link" className="shrink-0 inline-flex items-center gap-2 bg-[#111] dark:bg-white text-white dark:text-[#111] rounded-full px-6 py-3 text-sm font-medium hover:bg-neutral-800 dark:hover:bg-neutral-200 transition-colors whitespace-nowrap">
            {t.trustCenterTeaser.cta} <ArrowRight className="w-4 h-4 rtl:rotate-180" />
          </Link>
        </Reveal>
      </section>

      <section className="px-6 pb-16 sm:pb-20 lg:pb-28">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-px bg-neutral-200 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden" data-testid="trust-stats">
          {t.trustStats.map(([v, l], i) => {
            const Icon = TRUST_STAT_ICONS[i];
            return (
              <Reveal key={l} delay={i * 80} className="bg-white dark:bg-neutral-900 p-8">
                <Icon className="w-5 h-5 text-neutral-400 dark:text-neutral-500 mb-4" />
                <div className="font-heading text-4xl font-light"><StatValue value={v} /></div>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2 leading-relaxed">{l}</p>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="px-6 pb-16 sm:pb-20 lg:pb-28">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400 mb-4 text-center">{t.whereWeOperate.eyebrow}</p>
            <h2 className="font-heading font-light text-3xl tracking-tight leading-snug max-w-2xl text-center mx-auto">{t.whereWeOperate.heading}</h2>
            <p className="mt-5 text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed max-w-2xl text-center mx-auto">{t.whereWeOperate.text}</p>
          </Reveal>
          <Reveal delay={150} className="mt-12">
            <RenderWhenNear minHeight={360}><IndiaBranchMap /></RenderWhenNear>
          </Reveal>
        </div>
      </section>

      <section className="px-6 pb-16 sm:pb-20 lg:pb-28">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10">
          <Reveal className="md:col-span-4">
            <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400 mb-4">{t.dimensions.eyebrow}</p>
            <h2 className="font-heading font-light text-3xl tracking-tight leading-snug">{t.dimensions.heading}<br />{t.dimensions.headingAccent}</h2>
            <p className="mt-5 text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed">{t.dimensions.intro}</p>
          </Reveal>
          <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="dimensions-grid">
            {t.dimensions.items.map((d, i) => (
              <DimensionCard key={d.name} d={d} i={i} />
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 pb-16 sm:pb-20 lg:pb-28">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400 mb-4">{t.rollout.eyebrow}</p>
            <h2 className="font-heading font-light text-lg max-w-lg text-neutral-600 dark:text-neutral-400">{t.rollout.heading}</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-14" data-testid="rollout-timeline">
            {t.rollout.items.map((r, i) => {
              const Icon = ROLLOUT_ICONS[i];
              return (
                <Reveal key={r.when} delay={i * 100} className="relative pt-2">
                  <div className="absolute top-6 left-0 right-0 h-px bg-neutral-200 dark:bg-neutral-800" />
                  <span className="relative z-10 w-11 h-11 rounded-full bg-white dark:bg-neutral-950 border border-neutral-300 dark:border-neutral-700 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-[#111] dark:text-white" />
                  </span>
                  <p className="font-mono-data text-xs text-neutral-500 dark:text-neutral-400 mt-3">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="text-sm font-medium mt-1.5">{r.when}</h3>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed mt-2">{r.what}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative px-6 py-16 sm:py-20 lg:py-28 bg-[#111] dark:bg-black text-[#ededed] overflow-hidden">
        <div className="pointer-events-none absolute -bottom-40 left-1/3 w-[560px] h-[560px] rounded-full aurora-b opacity-40" aria-hidden="true" />
        <div className="relative max-w-6xl mx-auto">
          <Reveal>
            <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-400 mb-4">{t.testimonials.eyebrow}</p>
            <h2 className="font-heading font-light text-lg max-w-lg text-neutral-300">{t.testimonials.heading}</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14" data-testid="testimonials-grid">
            {t.testimonials.items.map((tm, i) => (
              <Reveal key={tm.name} delay={i * 100} as="figure" className="bg-[#161616] border border-neutral-800 rounded-lg p-8 flex flex-col justify-between hover:-translate-y-0.5 transition-transform duration-200">
                <div>
                  <Quote className="w-6 h-6 text-neutral-700 mb-4" fill="currentColor" />
                  <blockquote className="font-serif-accent italic text-lg leading-relaxed text-neutral-200">"{tm.quote}"</blockquote>
                </div>
                <figcaption className="mt-8 pt-5 border-t border-neutral-800 flex items-center gap-3">
                  <span className="w-9 h-9 rounded-full bg-neutral-800 flex items-center justify-center text-xs font-medium text-neutral-300 shrink-0">
                    {tm.name.charAt(0)}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-white">{tm.name}</p>
                    <p className="text-xs text-neutral-400 mt-0.5">{tm.role}</p>
                  </div>
                </figcaption>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-16 sm:py-20 lg:py-28">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400 mb-4">{t.faq.eyebrow}</p>
            <h2 className="font-heading font-light text-3xl tracking-tight leading-snug">{t.faq.heading}</h2>
            <p className="mt-5 text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed">{t.faq.introPrefix}<a href="mailto:privacy@exopilot.io" className="text-[#111] dark:text-white underline underline-offset-4">{t.faq.writeToUs}</a>{t.faq.orRead}<Link to="/privacy" className="text-[#111] dark:text-white underline underline-offset-4">{t.faq.fullPolicy}</Link>.</p>
          </Reveal>
          <Accordion type="single" collapsible className="w-full mt-10" data-testid="faq-accordion">
            {t.faq.items.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-neutral-200 dark:border-neutral-800">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left rtl:text-right text-sm font-medium hover:no-underline py-5">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed pb-6">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="px-6 pb-16 sm:pb-20 lg:pb-28" id="hotels" data-testid="hotels-use-case">
        <div className="max-w-6xl mx-auto">
          <Reveal className="relative rounded-lg overflow-hidden border border-neutral-200 dark:border-neutral-800 mb-14 aspect-[4/5] sm:aspect-[16/9] lg:aspect-[21/9]">
            <img loading="lazy" decoding="async" width="1200" height="800" src={productHotelsBanner} alt="EXOPILOT pendant on a hotel lobby table" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 sm:bottom-8 sm:left-8 sm:right-8">
              <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-white/70 mb-2 sm:mb-3">{t.hotels.eyebrow}</p>
              <h2 className="font-heading font-light text-2xl sm:text-3xl lg:text-4xl tracking-tight text-white max-w-2xl leading-snug">{t.hotels.heading}</h2>
            </div>
          </Reveal>

          <Reveal as="p" className="text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed max-w-3xl">{t.hotels.intro}</Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-10" data-testid="hotels-use-case-cards">
            {t.hotels.cards.map((c, i) => (
              <Reveal key={c.title} delay={i * 90} className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg p-7">
                {i === 0 && <img loading="lazy" decoding="async" width="1200" height="800" src={productFrontDesk} alt="" className="w-full aspect-[3/2] object-cover rounded-md mb-5" />}
                {i === 1 && <img loading="lazy" decoding="async" width="1200" height="800" src={productConcierge} alt="" className="w-full aspect-[3/2] object-cover rounded-md mb-5" />}
                {i === 2 && <img loading="lazy" decoding="async" width="1200" height="800" src={productFnbDining} alt="" className="w-full aspect-[3/2] object-cover rounded-md mb-5" />}
                <h3 className="text-sm font-medium">{c.title}</h3>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed mt-2">{c.text}</p>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-14 bg-[#111] dark:bg-black text-white rounded-lg p-9 md:p-12">
            <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-400 mb-8">{t.hotels.valueLabel}</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
              {t.hotels.values.map((v, i) => {
                const Icon = [TrendingUp, CloudOff, ClipboardCheck][i];
                return (
                  <div key={v.text}>
                    <Icon className="w-5 h-5 text-neutral-500 mb-4" />
                    <div className="font-heading text-4xl font-light"><StatValue value={v.stat} /></div>
                    <p className="text-sm text-neutral-400 leading-relaxed mt-3">{v.text}</p>
                  </div>
                );
              })}
            </div>
          </Reveal>

          <div className="flex justify-center mt-10">
            <button onClick={openDemo} data-testid="hotels-book-demo-cta"
              className="inline-flex items-center gap-2 bg-[#111] dark:bg-white text-white dark:text-[#111] rounded-full px-7 py-3.5 text-sm font-medium hover:bg-neutral-800 dark:hover:bg-neutral-200 transition-colors">
              {t.hotels.cta} <ArrowRight className="w-4 h-4 rtl:rotate-180" />
            </button>
          </div>
        </div>
      </section>

      <section className="px-6 pb-16 sm:pb-20 lg:pb-28">
        <Reveal as="div" className="max-w-6xl mx-auto bg-[#111] dark:bg-white text-white dark:text-[#111] rounded-lg p-9 md:p-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <h2 className="font-heading font-light text-3xl md:text-4xl tracking-tight">{t.finalCta.heading}</h2>
            <p className="text-neutral-400 dark:text-neutral-500 text-sm mt-3">{t.finalCta.text}</p>
          </div>
          <div className="shrink-0 flex flex-col sm:flex-row gap-3">
            <Link to="/register" data-testid="footer-cta" className="inline-flex items-center justify-center gap-2 bg-white dark:bg-[#111] text-[#111] dark:text-white rounded-full px-7 py-3.5 text-sm font-medium hover:bg-neutral-200 dark:hover:bg-neutral-800 transition-colors whitespace-nowrap">
              {t.finalCta.ctaGetStarted} <ArrowRight className="w-4 h-4 rtl:rotate-180" />
            </Link>
            <button onClick={openDemo} data-testid="final-book-demo-cta"
              className="inline-flex items-center justify-center gap-2 border border-white/30 dark:border-neutral-300 text-white dark:text-[#111] rounded-full px-7 py-3.5 text-sm font-medium hover:bg-white/10 dark:hover:bg-neutral-100 transition-colors whitespace-nowrap">
              <CalendarClock className="w-4 h-4" /> {t.finalCta.ctaBookDemo}
            </button>
          </div>
        </Reveal>
      </section>

      <section id="contact" className="px-6 pb-16 sm:pb-20 lg:pb-28">
        <Reveal as="div" className="max-w-4xl mx-auto bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg p-8 sm:p-10 md:p-12">
          <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400 mb-4">{t.contact.eyebrow}</p>
          <h2 className="font-heading font-light text-3xl tracking-tight leading-snug">{t.contact.heading}</h2>
          <p className="mt-5 text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed">{t.contact.text}</p>
          <form onSubmit={submitContact} data-testid="contact-form" className="mt-8 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400 uppercase tracking-wide">{t.contact.nameLabel}</label>
                <input data-testid="contact-name-input" required value={contact.name} onChange={setContactField("name")} placeholder={t.contact.namePlaceholder}
                  className="mt-1.5 w-full bg-[#f9f9f8] dark:bg-neutral-950 border border-neutral-300 dark:border-neutral-700 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#111] dark:focus:ring-white focus:border-transparent" />
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400 uppercase tracking-wide">{t.contact.emailLabel}</label>
                <input data-testid="contact-email-input" type="email" required value={contact.email} onChange={setContactField("email")} placeholder={t.contact.emailPlaceholder}
                  className="mt-1.5 w-full bg-[#f9f9f8] dark:bg-neutral-950 border border-neutral-300 dark:border-neutral-700 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#111] dark:focus:ring-white focus:border-transparent" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400 uppercase tracking-wide">{t.contact.messageLabel}</label>
              <textarea data-testid="contact-message-input" required rows={4} value={contact.message} onChange={setContactField("message")} placeholder={t.contact.messagePlaceholder}
                className="mt-1.5 w-full bg-[#f9f9f8] dark:bg-neutral-950 border border-neutral-300 dark:border-neutral-700 rounded-md px-3.5 py-2.5 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#111] dark:focus:ring-white focus:border-transparent resize-none" />
            </div>
            <button type="submit" data-testid="contact-submit-button" disabled={sendingContact}
              className="inline-flex items-center gap-2 bg-[#111] dark:bg-white text-white dark:text-[#111] rounded-full px-7 py-3 text-sm font-medium hover:bg-neutral-800 dark:hover:bg-neutral-200 transition-colors disabled:opacity-60">
              {sendingContact ? t.contact.sending : t.contact.send} <ArrowRight className="w-4 h-4 rtl:rotate-180" />
            </button>
          </form>
        </Reveal>
      </section>

      </main>
      <footer className="border-t border-neutral-200 dark:border-neutral-800 px-6 py-12">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <Logo />
          <nav className="flex flex-wrap gap-4 sm:gap-6 text-sm text-neutral-500 dark:text-neutral-400">
            <a href="#how" className="hover:text-[#111] dark:hover:text-white transition-colors">{t.footer.howItWorks}</a>
            <a href="#platform" className="hover:text-[#111] dark:hover:text-white transition-colors">{t.footer.platform}</a>
            <a href="#contact" className="hover:text-[#111] dark:hover:text-white transition-colors">{t.footer.contact}</a>
            <Link to="/trust" data-testid="footer-trust-link" className="hover:text-[#111] dark:hover:text-white transition-colors">{t.footer.trust}</Link>
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-[#111] dark:hover:text-white transition-colors">{t.footer.privacy}</Link>
            <Link to="/cookies" data-testid="footer-cookies-link" className="hover:text-[#111] dark:hover:text-white transition-colors">{t.footer.cookies}</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="hover:text-[#111] dark:hover:text-white transition-colors">{t.footer.terms}</Link>
          </nav>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">{t.footer.tagline}</p>
        </div>
      </footer>

      <StickyBookDemo />
      {idleReady && <Suspense fallback={null}><ChatWidget /><BookDemoModal /></Suspense>}
    </div>
  );
}

export default function Landing() {
  return (
    <BookDemoProvider>
      <LandingInner />
    </BookDemoProvider>
  );
}
